import { Image as KonvaImage, Stage, Layer, Rect } from "react-konva";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FloorplanDeviceStatusDialog } from "pages/jobs/dialogs/FloorplanDeviceStatusDialog";
import { FloorplansType, InspectionInputType, SpecificationType } from "operations/schema/schema";
import * as Icons from "../components/FloorplanIcons/index";
import { useAppDispatch, useAppSelector } from "store";
import { selectSelectedJobVisit, updateInspection } from "store/slices/jobs.store";
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { DeviceTestStatusEnum } from "pages/jobs/jobDetails/visit/inspection/DeviceTestStatusEnum";
import { FormattedMessage } from "react-intl";

interface FloorplanCanvasProps {
  floor: string;
  floorplans: FloorplansType[];
  specifications: SpecificationType[];
}

const PADDING = { top: 0, bottom: 0, left: 0, right: 0 };

const arrayBufferToBase64 = (buffer: ArrayBuffer | number[]) => {
  const uint8Array = new Uint8Array(buffer);
  const blob = new Blob([uint8Array], { type: 'image/png' });
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

const getRingColor = (
  spec: SpecificationType,
  inspections: InspectionInputType[],
  showTestResult: boolean
): string => {
  if (showTestResult) {
    const testResult = inspections.find(
      (inspection) => String(inspection.specification?.id) === String(spec.id)
    )?.testResult;
  
    if (testResult) {
      switch (testResult) {
        case DeviceTestStatusEnum.Pass:
          return "#4CAF50";
        case DeviceTestStatusEnum.Fail:
          return "#F44336";
        case DeviceTestStatusEnum.NotTested:
          return "#9E9E9E";
      }
    }
  }
  const result = (showTestResult
    ? inspections.find((inspection) => String(inspection.specification?.id) === String(spec.id))?.testResult?.toString()
    : spec.resultOfLastService
  )?.trim().toLowerCase() ?? "not tested";

  return {
    pass: "#4CAF50",
    fail: "#F44336",
    "not tested": "#9E9E9E",
  }[result] ?? "#9E9E9E";
};

export const FloorplanCanvas: React.FC<FloorplanCanvasProps> = ({
  floor,
  floorplans,
  specifications,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [selectedDevice, setSelectedDevice] = useState<SpecificationType | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const [imageOriginalSize, setImageOriginalSize] = useState({ width: 1, height: 1 });
  const dispatch = useAppDispatch();
  const selectedJobVisit = useAppSelector(selectSelectedJobVisit);
  const inspections = selectedJobVisit?.inspections || [];
  const [imageScale, setImageScale] = useState(1);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [showTestResult, setShowTestResult] = useState(false); 
  const [initialZoom, setInitialZoom] = useState<{ scale: number; position: { x: number; y: number } }>({
    scale: 1,
    position: { x: 0, y: 0 },
  });
  const baseIconSize = 45;
  const iconSize = baseIconSize;
  const halfSize = iconSize / 2;
  const baseOffsetX = (-iconSize / 2) + 50;
  const baseOffsetY = (-iconSize / 2) + 50;
  const offsetX = baseOffsetX * imageScale;
  const offsetY = baseOffsetY * imageScale;

  const handleImageZoom = (e: any) => {
    e.evt.preventDefault();
    const scaleBy = 1.05;
    const direction = e.evt.deltaY > 0 ? -1 : 1;
    const newScale = imageScale * (direction > 0 ? scaleBy : 1 / scaleBy);
    setImageScale(Math.min(5, Math.max(0.2, newScale)));
  };

  const selectedFloorplan = useMemo(
    () => floorplans.find((fp) => fp.floorName === floor),
    [floor, floorplans]
  );

  const floorGuid = selectedFloorplan?.id.toString();

  const specificationsOnFloor = useMemo(() => {
    return specifications.filter((spec) => {
      const specId = spec.floorPlanImageId?.toString();
      const hasCoordinates = spec.xCoordinate != null && spec.yCoordinate != null;
      return specId === floorGuid && hasCoordinates;
    });
  }, [specifications, floorGuid]);

  useEffect(() => {
    const resize = () => {
      if (containerRef.current) {
        const { clientWidth, clientHeight } = containerRef.current;
        setDimensions({
          width: clientWidth - PADDING.left - PADDING.right,
          height: clientHeight - PADDING.top - PADDING.bottom,
        });
      }
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  useEffect(() => {
    if (image && dimensions.width > 0 && dimensions.height > 0) {
      const scale = (dimensions.height * 0.75) / image.height;
      const scaledWidth = image.width * scale;
      const posX = (dimensions.width - scaledWidth) / 2;
      const posY = (dimensions.height - image.height * scale) / 2;

      setImageScale(scale);
      setImagePosition({ x: posX, y: posY });
      setInitialZoom({ scale, position: { x: posX, y: posY } });

      setImageDimensions({
        width: scaledWidth,
        height: image.height * scale,
      });
    }
  }, [image, dimensions]);

  useEffect(() => {
    if (selectedFloorplan) {
      arrayBufferToBase64(selectedFloorplan.data).then((base64) => {
        const img = new Image();
        img.src = base64;
        img.onload = () => {
          setImage(img);
          setImageOriginalSize({ width: img.width, height: img.height });
        };
        img.onerror = (err) => {
          console.error("[Canvas] Failed to load image", err);
          setImage(null);
        };
      });
    }
  }, [selectedFloorplan]);

  const squareSize = 15;
  const colors = ["#eeeeee", "#cccccc"];
  const checkerboard = [];
  for (let y = 0; y < dimensions.height; y += squareSize) {
    for (let x = 0; x < dimensions.width; x += squareSize) {
      const isEven = ((x / squareSize) + (y / squareSize)) % 2 === 0;
      checkerboard.push(
        <Rect
          key={`${x}-${y}`}
          x={x}
          y={y}
          width={squareSize}
          height={squareSize}
          fill={colors[isEven ? 0 : 1]}
        />
      );
    }
  }

  const scaleX = imageOriginalSize.width > 0 ? imageDimensions.width / imageOriginalSize.width : 1;
  const scaleY = imageOriginalSize.height > 0 ? imageDimensions.height / imageOriginalSize.height : 1;

  function isIconKey(key: string): key is keyof typeof Icons {
    return key in Icons;
  }

  const isAtOriginalPosition =
    Math.abs(imagePosition.x - initialZoom.position.x) < 0.5 &&
    Math.abs(imagePosition.y - initialZoom.position.y) < 0.5;
  const isAtOriginalScale = Math.abs(imageScale - initialZoom.scale) < 0.01;

  const handleRecenter = () => {
    setImageScale(initialZoom.scale);
    setImagePosition(initialZoom.position);
  };

  const zoomStep = 1.2;

  const handleZoomIn = () => {
    setImageScale((prev) => Math.min(prev * zoomStep, 5));
  };

  const handleZoomOut = () => {
    setImageScale((prev) => Math.max(prev / zoomStep, 0.2));
  };

  return (
    <div ref={containerRef} style={{ width: "100%", height: "100%", position: "relative" }}>
      <div
        style={{
          paddingTop: PADDING.top,
          paddingBottom: PADDING.bottom,
          paddingLeft: PADDING.left,
          paddingRight: PADDING.right,
          width: "100%",
          height: "100%",
          boxSizing: "border-box",
          position: "relative",
        }}
      >
        <div style={{ position: 'absolute', top: 4, right: 4, zIndex: 1000 }}>
         <ToggleButtonGroup
            value={showTestResult ? 'testResult' : 'lastService'}
            exclusive
            onChange={(_, value) => setShowTestResult(value === 'testResult')}
            size="small"
            sx={{
              borderRadius: 2,
              boxShadow: '0 2px 6px rgba(0,0,0,0.1)', 
              bgcolor: '#fff',
              '& .MuiToggleButton-root': {
              textTransform: 'none',
              fontWeight: 570,
              color: '#555',
              fontFamily: 'Roboto, sans-serif',
              fontSize: '0.875rem',
              border: '1px solid #ccc',
              transition: 'all 0.3s ease',
              minWidth: '130px', 
              justifyContent: 'center',
              flex: 1, 
                '&:not(:last-of-type)': {
                  borderRight: 0, 
                },
                '&:hover': {
                  bgcolor: '#f0f0f0',
                  color: '#0A73BA',
                  borderColor: '#0A73BA',
                },
                '&.Mui-selected': {
                  bgcolor: '#0A73BA',
                  color: '#fff',
                  borderColor: '#0A73BA',
                  '&:hover': {
                    bgcolor: '#095a8e',
                    borderColor: '#095a8e',
                  },
                },
              },
            }}
          >
          <ToggleButton value="lastService">
            <FormattedMessage id="canvas.lastServiceResults" />
          </ToggleButton>
          <ToggleButton value="testResult">
            <FormattedMessage id="canvas.currentServiceResults" />
          </ToggleButton>
        </ToggleButtonGroup>
        </div>
        <Stage
          width={dimensions.width}
          height={dimensions.height}
          onWheel={handleImageZoom}
        >
          <Layer listening={false}>{checkerboard}</Layer>
          <Layer
            draggable
            x={imagePosition.x}
            y={imagePosition.y}
            scaleX={imageScale}
            scaleY={imageScale}
            onDragMove={(e) => {
              setImagePosition({
                x: e.target.x(),
                y: e.target.y(),
              });
            }}
            onDragEnd={(e) => {
              setImagePosition({
                x: e.target.x(),
                y: e.target.y(),
              });
            }}
          >
            {image && (
              <KonvaImage
                image={image}
                width={imageDimensions.width}
                height={imageDimensions.height}
              />
            )}
          </Layer>
        </Stage>
        <div
          style={{
            position: "fixed",
            bottom: 20,
            left: 20,
            display: "flex",
            flexDirection: "column",
            gap: 10,
            zIndex: 1000,
          }}
        >
          <button
            onClick={handleZoomIn}
            style={{
              width: 55,
              height: 55,
              borderRadius: "50%",
              backgroundColor: "#0A73BA",
              color: "white",
              fontSize: 24,
              border: "none",
              cursor: "pointer",
              boxShadow: "0 2px 5px rgba(0,0,0,0.3)",
            }}
            title="Zoom In"
          >
            <ZoomInIcon style={{ fontSize: 24 }} />
          </button>
          <button
            onClick={handleZoomOut}
            style={{
              width: 55,
              height: 55,
              borderRadius: "50%",
              backgroundColor: "#0A73BA",
              color: "white",
              fontSize: 24,
              border: "none",
              cursor: "pointer",
              boxShadow: "0 2px 5px rgba(0,0,0,0.3)",
            }}
            title="Zoom Out"
          >
            <ZoomOutIcon style={{ fontSize: 24 }} />
          </button>
          <button
            onClick={handleRecenter}
            disabled={isAtOriginalPosition && isAtOriginalScale}
            style={{
              width: 55,
              height: 55,
              borderRadius: "50%",
              backgroundColor: isAtOriginalPosition && isAtOriginalScale ? "" : "#0A73BA",
              color: isAtOriginalPosition && isAtOriginalScale ? "#9e9e9e" : "white",
              border: "none",
              cursor: isAtOriginalPosition && isAtOriginalScale ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 0,
            }}
            title={
              isAtOriginalPosition && isAtOriginalScale
                ? "Image is already centered and zoomed"
                : "Recenter image"
            }
          >
            <CenterFocusStrongIcon style={{ fontSize: 28 }} />
          </button>
        </div>
        {specificationsOnFloor.map((spec, index) => {
          const iconKey = `${spec.icon}Icon`;
          const IconComponent = isIconKey(iconKey) ? Icons[iconKey] : Icons["ShieldIcon"];
          const x = spec.xCoordinate! * scaleX;
          const y = spec.yCoordinate! * scaleY;
          const rawLeft = imagePosition.x + (x * imageScale) + PADDING.left + offsetX;
          const rawTop = imagePosition.y + (y * imageScale) + PADDING.top + offsetY;
          const isOutside =
            rawLeft + halfSize < 0 ||
            rawLeft - halfSize > dimensions.width ||
            rawTop + halfSize < 0 ||
            rawTop - halfSize > dimensions.height;
          const visibility = isOutside ? "hidden" : "visible";
          return (
          <div
            key={index}
            style={{
              position: "absolute",
              top: rawTop,
              left: rawLeft,
              transform: "translate(-50%, -50%)",
              cursor: "pointer",
              width: iconSize,
              height: iconSize,
              userSelect: "none",
              visibility,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
            onClick={() => {
              setSelectedDevice({ ...spec, checklists: spec.checklists || null });
              setDialogOpen(true);
            }}
          >
            <div
              style={{
                width: iconSize,
                height: iconSize,
                borderRadius: "50%",
                boxShadow: `0 0 0 4px ${getRingColor(spec, inspections, showTestResult)}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "transparent",
              }}
            >
              <IconComponent strokeColor="#ffffff" />
            </div>
          </div>
        );
        })}
      </div>
      {selectedDevice && (
        <FloorplanDeviceStatusDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          device={selectedDevice}
          onSave={(updatedDevice) => {
            dispatch(updateInspection({ inspection: updatedDevice }));
            setSelectedDevice(updatedDevice);
          }}
        />
      )}
    </div>
  );
};